/**
 * TypeScript type definitions for the OTDR Device Management UI
 */

// ============================================================================
// Authentication Types
// ============================================================================

export interface AuthUser {
  id: string
  email: string
  displayName: string
  customerId: string | null
  roles: string[]
  registrationComplete: boolean
  // New field: true if this is the first user from their email domain
  isFirstUserForDomain?: boolean
  registrationStatus?: 'complete' | 'pending_domain_approval' | 'pending_user_approval' | 'rejected' | null
  // Token info
  accessToken?: string
}

export interface RegistrationRequest {
  displayName: string
}

export interface DomainCheckResponse {
  allowed: boolean
  domain: string
  customerId?: string
}

export interface PendingDomain {
  userId: string
  email: string
  emailDomain: string
  displayName: string
  customerId: string
  requestedAt: string
}

export interface PendingUser {
  userId: string
  email: string
  displayName: string
  requestedAt: string
}

// ============================================================================
// Device Types
// ============================================================================

export interface Device {
  deviceId: string
  claimed: boolean
  customerId: string | null
  claimedByUserId: string | null
  claimedAt: string | null
  lastActivityTime: string | null
  connectionState: 'Connected' | 'Disconnected' | string
  enabled?: boolean
  statusMessage?: string
  tags?: Record<string, unknown>
  reportedProperties?: Record<string, unknown>
  properties?: DeviceProperties
  // Alias for backward compatibility
  claimedBy?: string | null
  status?: string
  // Async claim support
  claimStatus?: 'unclaimed' | 'pending' | 'claimed' | 'denied'
  claimInitiatedAt?: string | null
  deviceResponse?: string | null
}

export interface DeviceProperties {
  manufacturer?: string
  model?: string
  firmwareVersion?: string
  serialNumber?: string
  location?: string
  friendlyName?: string
  [key: string]: string | undefined
}

export interface DeviceListResponse {
  devices: Device[]
  totalCount: number
  page: number
  pageSize: number
  continuationToken?: string
  hasMore: boolean
}

// ============================================================================
// Pending Claims Types (async device claiming)
// ============================================================================

export interface PendingClaim {
  deviceId: string
  customerId: string
  claimedByUserId: string
  claimedByUserName?: string
  claimedByUserEmail?: string
  claimInitiatedAt: string
  expiresAt: string
  claimStatus: 'pending' | 'denied'
  deviceResponse?: string
  connectionState?: string
  friendlyName?: string
}

export interface PendingClaimsResponse {
  pendingClaims: PendingClaim[]
  deniedClaims: PendingClaim[]
  totalPending: number
  totalDenied: number
}

export interface ClaimModeResponse {
  syncMode: boolean
  pendingClaimTimeoutMinutes: number
  pollIntervalSeconds: number
}

// ============================================================================
// User Types
// ============================================================================

export interface User {
  id: string
  email: string
  displayName: string
  customerId: string
  roles: string[]
  createdAt: string
  lastLoginAt: string | null
  enabled: boolean
}

export interface UserListResponse {
  users: User[]
  total: number
  page: number
  pageSize: number
}

export interface UpdateUserRolesRequest {
  roles: string[]
}

// ============================================================================
// Role Definitions
// ============================================================================

export const Roles = {
  SUPER_ADMIN: 'SuperAdmin',
  CUSTOMER_ADMIN: 'CustomerAdmin',
  DEVICE_ADMIN: 'DeviceAdmin',
  FILE_ADMIN: 'FileAdmin',
  DEVICE_USER: 'DeviceUser',
  FILE_USER: 'FileUser',
  SOR_ANALYST: 'SorAnalyst',
} as const

export type Role = typeof Roles[keyof typeof Roles]

export const AdminRoles = [Roles.SUPER_ADMIN, Roles.CUSTOMER_ADMIN, Roles.DEVICE_ADMIN, Roles.FILE_ADMIN] as const
export const UserRoles = [Roles.DEVICE_USER, Roles.FILE_USER] as const
export const RestrictedRoles = [Roles.SUPER_ADMIN, Roles.SOR_ANALYST] as const

// ============================================================================
// API Response Types
// ============================================================================

export interface ApiError {
  message: string
  code?: string
  status?: number
  details?: Record<string, unknown>
}

export interface ApiResponse<T> {
  data: T
  success: boolean
  message?: string
}

// ============================================================================
// Dashboard Types
// ============================================================================

export interface DashboardStats {
  totalDevices: number
  claimedDevices: number
  onlineDevices: number
  totalUsers: number
}

export type TimePeriod = 'day' | 'week' | 'month'

// ============================================================================
// SOR File Types (Backend DTOs - file_upload_svc)
// ============================================================================

/**
 * File metadata from file_upload_svc backend
 */
export interface SorFileDto {
  name: string              // Full blob path (e.g., "deviceId/testId/filename.sor")
  fileName: string          // Just the filename
  size: number
  contentType: string
  createdAt: string         // ISO 8601
  lastModified: string      // ISO 8601
  deviceId: string
  customerId: string
  testId: string
  locationA: string
  locationB: string
  metadata: Record<string, string>
  tags: Record<string, string>
  contentMd5: string
  etag: string
}

/**
 * Paginated file list response
 */
export interface SorFileListResponse {
  files: SorFileDto[]
  totalCount: number
  continuationToken: string | null
  pageSize: number
  customerId: string
}

/**
 * SAS URL response for download/upload operations
 */
export interface SasUrlResponse {
  sasUrl: string
  blobName: string
  containerName: string
  customerId: string
  expiresAt: string
  ttlMinutes: number
  operationType: 'download' | 'upload' | 'delete'
}

/**
 * Delete file response
 */
export interface DeleteFileResponse {
  success: boolean
  blobName: string
  message: string
}

/**
 * Query parameters for file listing
 */
export interface FileListParams {
  customerId?: string
  prefix?: string
  deviceId?: string
  pageSize?: number
  continuationToken?: string
}

/**
 * Container-level SAS URL response for direct blob access
 */
export interface ContainerSasResponse {
  sasUrl: string
  containerName: string
  customerId: string
  permissions: string        // "rlf" or "rlfd"
  expiresAt: string
  ttlMinutes: number
  canDelete: boolean
}

/**
 * Filter options for blob tag queries
 */
export interface BlobTagFilter {
  deviceId?: string
  testId?: string
  locationA?: string
  locationB?: string
  yyyy?: string              // Year tag (e.g., "2025")
  MM?: string                // Month tag (e.g., "01")
  dd?: string                // Day tag (e.g., "14")
  // Date range on last_modified_on_device tag (ISO 8601 strings)
  lastModifiedFrom?: string
  lastModifiedTo?: string
  // Date range on uploaded_at tag (ISO 8601 strings)
  uploadedFrom?: string
  uploadedTo?: string
  model?: string             // Band model tag (e.g., "BREAKFIX-CBAND-OTDR")
}

/**
 * Request for generating upload SAS URL
 */
export interface UploadSasRequest {
  blobName: string
  contentType?: string
  metadata?: Record<string, string>
  ttlMinutes?: number
}

// ============================================================================
// SOR Analysis Types - BiDi OTDR Analysis
// ============================================================================

/**
 * Individual trace point (distance in km, power in dB)
 */
export interface TracePoint {
  x: number  // distance in km
  y: number  // power in dB
}

/**
 * Response from trace upload
 */
export interface TraceUploadResponse {
  traceId: string
  filename: string
  traceType: 'SOP' | 'EOP' | 'CMP' | string
  fileSize: number
  uploadTime: string
  metadata: Record<string, unknown>
  points: TracePoint[]
}

/**
 * Response from trace alignment
 */
export interface AlignmentResponse {
  shift: number
  sessionId: string
  alignedTrace: TraceUploadResponse
}

/**
 * BiDi analysis result (attenuation and backscatter traces)
 */
export interface BiDiAnalysisResult {
  success: boolean
  sessionId: string
  attenuationTrace: TraceUploadResponse
  backscatterTrace: TraceUploadResponse
}

/**
 * Individual fiber segment from fiber analysis
 */
export interface FiberSegment {
  index: number
  startDistance: number
  endDistance: number
  loss: number
  points: TracePoint[]
}

/**
 * Splice analysis result from API
 * API returns: { spliceId: "Splice 0", loss: number, stdDeviation: number }
 */
export interface SpliceAnalysis {
  spliceId: string
  loss: number
  stdDeviation: number
}

/**
 * Splice marker from fiber analysis
 */
export interface SpliceMarker {
  x: number
  y: number
  index: number
}

/**
 * Individual fibre data from API
 * API returns: { fibreId: "Fibre 0", loss: number, length: number, stdDeviation: number }
 */
export interface FibreAnalysisData {
  fibreId: string
  loss: number
  length: number
  stdDeviation: number
}

/**
 * Fiber analysis (show fibres) result
 * API returns fibreAnalysis as array of FibreAnalysisData objects
 */
export interface FiberAnalysisResult {
  success: boolean
  sessionId: string
  individualFibres: TracePoint[][]
  spliceMarkers: SpliceMarker[]
  fibreAnalysis: FibreAnalysisData[]
}

/**
 * Splice loss estimation result
 */
export interface SpliceLossResult {
  success: boolean
  sessionId: string
  spliceAnalysis: SpliceAnalysis[]
}

/**
 * Event/imperfection detection from API
 * API returns: { eventId: "0-1", distance: number, loss: number }
 */
export interface EventDetection {
  eventId: string
  distance: number
  loss: number
}

/**
 * Event position marker for chart display
 */
export interface EventPosition {
  x: number
  y: number
}

/**
 * Event detection result
 * Note: eventPositions is [{x, y}, ...] format from the API
 */
export interface EventDetectionResult {
  success: boolean
  sessionId: string
  eventAnalysis: EventDetection[]
  eventPositions: EventPosition[]
}

/**
 * Report generation result
 */
export interface ReportResult {
  success: boolean
  reportId: string
  reportName: string
  reportType: 'single' | 'batch'
  status: 'generating' | 'completed' | 'failed'
  generatedAt: string
}

/**
 * Analysis session state
 */
export interface AnalysisSession {
  sessionId: string
  sopTrace?: TraceUploadResponse
  eopTrace?: TraceUploadResponse
  alignedEopTrace?: TraceUploadResponse
  alignmentShift?: number
  biDiResult?: BiDiAnalysisResult
  fiberResult?: FiberAnalysisResult
  spliceResult?: SpliceLossResult
  eventResult?: EventDetectionResult
  reports: ReportResult[]
  createdAt: string
}

/**
 * Analysis parameters (configurable)
 */
export interface AnalysisParameters {
  lFromSplice?: number       // Distance from splice for linear regression (km)
  linearFitSpan?: number     // Span for linear fit in splice loss calculation (km)
  minSplicingSpan?: number   // Minimum distance between splices (km)
  minSplicePeak?: number     // Minimum peak height for splice detection (dB)
  eventThreshold?: number    // Event detection threshold (dB)
}

// Legacy types for backward compatibility
export interface SorFile {
  id: string
  filename: string
  deviceId: string
  customerId: string
  uploadedAt: string
  fileSize: number
  status: 'pending' | 'processing' | 'analyzed' | 'error'
  analysisResult?: SorAnalysisResult
}

export interface SorAnalysisResult {
  fiberLength: number
  events: SorEvent[]
  overallLoss: number
  status: 'pass' | 'fail' | 'warning'
}

export interface SorEvent {
  position: number
  type: 'splice' | 'connector' | 'break' | 'reflection'
  loss: number
  reflectance?: number
}

// ============================================================================
// Fiber Config Types
// ============================================================================

export type BandModel = 'BREAKFIX-CBAND-OTDR' | 'BREAKFIX-LBAND-OTDR' | 'BREAKFIX-OBAND-OTDR'

export interface FiberConfigParam {
  name: string
  value: number | string
  group: string
  min: number
  max: number
  dataType: 'double' | 'int'
  step: number
}

export interface FiberConfig {
  buttonName: string       // Unique identifier, maps to XML buttonName attribute
  isBuiltIn: boolean       // SMF/HCF cannot be deleted or renamed
  checked: boolean         // Maps to XML checked attribute
  band: BandModel | null
  wavelengthIndex: number | null
  params: FiberConfigParam[]
  createTime: string
}

// ============================================================================
// Admin / SuperAdmin Types
// ============================================================================

export interface CustomerSummary {
  customerId: string
  emailDomain: string
  userCount: number
  adminCount: number
  deviceCount: number
  pendingClaimCount: number
  status: 'active' | 'suspended'
}

export interface CustomerListResponse {
  customers: CustomerSummary[]
  totalCustomers: number
  totalUsers: number
  totalDevices: number
  totalPendingClaims: number
  page: number
  size: number
  totalPages: number
}

export interface SuspendActivateResponse {
  succeeded: number
  failed: number
  failures?: { userId: string; error: string }[]
}

export interface CaCertificateInfo {
  subject: string
  issuer: string
  expiryDate: string
  notBefore: string
  thumbprint: string
  status: 'valid' | 'expiring' | 'expired'
  deviceCount?: number
}

export interface CaStatusResponse {
  onboardingCas: CaCertificateInfo[]
  intermediateCas: CaCertificateInfo[]
}

export interface DeviceCertificateStatus {
  deviceId: string
  customerId: string
  certExpiry: string | null
  rotationStatus: 'none' | 'requested' | 'in-progress' | 'completed' | 'failed'
  lastRotated: string | null
  issuerCn: string | null
}

export interface DeviceCertificateListResponse {
  devices: DeviceCertificateStatus[]
  total: number
  page: number
  size: number
}

export interface CertificateRotationBulkRequest {
  expiryDays: number
}
